import React, { useEffect, useState, useRef } from 'react';
import Calendar from 'react-calendar';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import calendarSlice from '../../store/slices/calendar';
import JWButton from '../brand/JWButton';
import 'react-calendar/dist/Calendar.css';
import './calendar.css';
import { PencilEditIcon, AddButtonIcon } from '../icons';
import { Button } from 'reactstrap';
import axiosService from '../../utils/axios';
import { EventType, Value } from '../../types/calendar';
import { ExternalLink, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import ClockIcon from '../../assets/icons/ClockIcon';
import LocationPinIcon from '../../assets/icons/LocationPinIcon';
import TrashIcon from '../../assets/icons/TrashIcon';

interface JWCalendarProps {
    calendarValue: Value;
    setCalendarValue: (value: Value) => void;
    eventsList: EventType[];
    view?: 'month' | 'year' | 'decade' | 'century';
    onViewChange?: (view: 'month' | 'year' | 'decade' | 'century') => void;
}

function isDate(value: Value): value is Date {
    return value instanceof Date;
}

function isSameDay(a: Date, b: Date) {
    return (
        a.getDate() === b.getDate() &&
        a.getMonth() === b.getMonth() &&
        a.getFullYear() === b.getFullYear()
    );
}

function isPastDate(date: Date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
}

const JWCalendar: React.FC<JWCalendarProps> = ({ calendarValue, setCalendarValue, eventsList, view: externalView, onViewChange }) => {
    const [activeStartDate, setActiveStartDate] = useState<Date | undefined>(undefined);
    const [internalView, setInternalView] = useState<'month' | 'year' | 'decade' | 'century'>('month');

    // Use either the external view (if controlled) or the internal view
    const currentView = externalView || internalView;
    const handleViewChange = onViewChange || setInternalView;

    // Update activeStartDate when calendarValue changes
    useEffect(() => {
        if (calendarValue instanceof Date) {
            setActiveStartDate(calendarValue);
        }
    }, [calendarValue]);

    function handleCalendarChange(newDate: Value) {
        setCalendarValue(newDate);
    }

    function tileClassName(pair: any) {
        if (pair.view !== 'month') {
            return;
        }

        // Check if date is in current month
        const isCurrentMonth = pair.date.getMonth() === pair.activeStartDate.getMonth();

        // Check if this is the selected date
        const isSelected = calendarValue instanceof Date && isSameDay(pair.date, calendarValue);

        let classNames = 'text-sm font-sans rounded-full ';

        // Apply styling based on date status
        if (isSelected) {
            // Selected date (already handled by react-calendar--active class, just ensure text is white)
            classNames += 'text-white ';
        } else if (!isCurrentMonth) {
            // Date from previous/next month - just gray text, no background
            classNames += 'text-gray-400 ';
        } else {
            // Current month date
            classNames += 'text-eerieblack ';
        }

        return classNames;
    }

    function tileContent(pair: any) {
        if (pair.view !== 'month') return null;

        if (!Array.isArray(eventsList) || eventsList.length === 0) return null;

        const dayEvents = eventsList.filter((event) =>
            isSameDay(new Date(event.dateTime), pair.date)
        );

        if (dayEvents.length === 0) return null;

        return (
            <div className="absolute bottom-1 left-0 right-0 flex justify-center">
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#EF4060' }} />
            </div>
        );
    }

    return (
        <div className="relative">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-eerieblack">Events</h2>
                <JWButton
                    size="small"
                    onClick={() => {
                        const today = new Date();
                        setCalendarValue(today);
                        setActiveStartDate(today);
                        handleViewChange('month'); // Return to day view
                    }}
                    className="text-[15px] bg-[#2A2B3C] text-white px-3 py-2 rounded-md hover:bg-[#373951] transition-colors flex items-center gap-2"
                >
                    <CalendarDays size={18} />
                    Today
                </JWButton>
            </div>
            <Calendar
                locale="en-US"
                tileClassName={tileClassName}
                tileContent={tileContent}
                onChange={handleCalendarChange}
                value={calendarValue}
                activeStartDate={activeStartDate}
                view={currentView}
                showFixedNumberOfWeeks={true}
                onViewChange={({ view }) => handleViewChange(view as 'month' | 'year' | 'decade' | 'century')}
                onActiveStartDateChange={({ activeStartDate }) => {
                    // We don't need complex logic here since the useEffect will handle it
                    if (activeStartDate) {
                        setActiveStartDate(activeStartDate);
                    }
                }}
                navigationLabel={({ date, label, locale, view }) => (
                    <p className="text-sm text-eerieblack font-sans rounded-full">
                        {label}{' '}
                    </p>
                )}
                next2Label={null}
                prev2Label={null}
                nextLabel={
                    <div className="text-sm text-eerieblack font-sans rounded-full">
                        {'>'}
                    </div>
                }
                prevLabel={
                    <div className="text-sm text-eerieblack font-sans rounded-full">
                        {'<'}
                    </div>
                }
                formatShortWeekday={(locale, date) =>
                    ['S', 'M', 'T', 'W', 'T', 'F', 'S'][date.getDay()]
                }
            />
        </div>
    );
};

function JWEventsList({
    calendarValue,
    setCalendarValue,
    createEvent,
    openEventModal,
    eventsList,
    fetchEvents,
    canEdit = false,
    setCalendarView = null,
}: {
    calendarValue: Value
    setCalendarValue: (value: Value) => void
    createEvent: () => void
    openEventModal: (event: EventType) => void
    eventsList: EventType[]
    fetchEvents: () => void
    canEdit?: boolean
    setCalendarView?: ((view: 'month' | 'year' | 'decade' | 'century') => void) | null
}) {
    const [dailyEvents, setDailyEvents] = useState<EventType[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasPrevEvent, setHasPrevEvent] = useState(false);
    const [hasNextEvent, setHasNextEvent] = useState(false);
    const dispatch = useDispatch();
    const calendarViewRef = useRef<React.Dispatch<React.SetStateAction<'month' | 'year' | 'decade' | 'century'>> | null>(null);

    // Set a ref to the parent JWCalendar component's setView function
    useEffect(() => {
        // Find the JWCalendar component
        const calendarComponent = document.querySelector('.react-calendar');
        if (calendarComponent) {
            const jwCalendar = (calendarComponent as any).__reactProps$;
            if (jwCalendar && jwCalendar.setView) {
                calendarViewRef.current = jwCalendar.setView;
            }
        }
    }, []);

    // Check if previous/next events exist and update dailyEvents
    useEffect(() => {
        if (!isDate(calendarValue) || !Array.isArray(eventsList)) {
            setHasPrevEvent(false);
            setHasNextEvent(false);
            setDailyEvents([]);
            return;
        }

        const currentTime = calendarValue.getTime();

        // Get the daily events
        const filteredDailyEvents = eventsList.filter(event =>
            isSameDay(new Date(event.dateTime), calendarValue)
        );
        setDailyEvents(filteredDailyEvents);

        // Find previous events
        const hasPrev = eventsList.some(event =>
            new Date(event.dateTime).getTime() < currentTime
        );
        setHasPrevEvent(hasPrev);

        // Find next events
        const hasNext = eventsList.some(event =>
            new Date(event.dateTime).getTime() > currentTime
        );
        setHasNextEvent(hasNext);
    }, [eventsList, calendarValue]);

    const navigateToEvent = (direction: 'next' | 'prev') => {
        if (!Array.isArray(eventsList) || eventsList.length === 0 || !isDate(calendarValue)) {
            return;
        }

        // Sort events by date
        const sortedEvents = [...eventsList].sort((a, b) =>
            new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
        );

        if (direction === 'next') {
            // Find the next event after the current date
            const nextEvent = sortedEvents.find(event =>
                new Date(event.dateTime).getTime() > calendarValue.getTime()
            );

            if (nextEvent) {
                const nextDate = new Date(nextEvent.dateTime);
                setCalendarValue(nextDate);
                // Reset to month/day view if the function was passed
                if (setCalendarView) {
                    setCalendarView('month');
                }
                dispatch(calendarSlice.actions.setIsoDate(nextDate.toISOString()));
            }
        } else {
            // Find the previous event before the current date
            const prevEvents = sortedEvents.filter(event =>
                new Date(event.dateTime).getTime() < calendarValue.getTime()
            );

            if (prevEvents.length > 0) {
                const lastPrevEvent = prevEvents[prevEvents.length - 1];
                const prevDate = new Date(lastPrevEvent.dateTime);
                setCalendarValue(prevDate);
                // Reset to month/day view if the function was passed
                if (setCalendarView) {
                    setCalendarView('month');
                }
                dispatch(calendarSlice.actions.setIsoDate(prevDate.toISOString()));
            }
        }
    };

    const defaultChildren = (
        canEdit ? (
            <div className="flex flex-col items-center justify-center h-full py-8">
                <p className="text-gray-500 text-center mb-4">
                    There are no events to display. Please choose a date on the
                    nearby calendar to see events for that day or click below to
                    create an event.
                </p>
                <JWButton
                    onClick={createEvent}
                    size="medium"
                    className="mt-2 text-[16px] flex items-center gap-2 px-6 py-3 bg-[#2A2B3C] text-white rounded-md hover:bg-[#373951] transition-colors"
                >
                    <AddButtonIcon width={20} height={20} fill="currentColor" />
                    Create Event
                </JWButton>
            </div>
        ) : (
            <div className="flex items-center justify-center h-full py-8">
                <p className="text-gray-500 text-center">
                    There are no events to display.
                </p>
            </div>
        )
    );

    const deleteEvent = async (event: EventType) => {
        setIsLoading(true);
        try {
            await axiosService.delete(`/events/${event._id}`);
            await fetchEvents();
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="jwEventsList md:w-1/2 w-full md:mr-36">
            <div className="flex flex-row justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-eerieblack">
                    {isDate(calendarValue)
                        ? calendarValue.toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        })
                        : ''}
                </h2>
                <div className="flex gap-2">
                    <JWButton
                        size="small"
                        onClick={() => navigateToEvent('prev')}
                        className={`text-[15px] ${hasPrevEvent ? 'bg-[#2A2B3C] text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'} px-3 py-2 rounded-md ${hasPrevEvent ? 'hover:bg-[#373951]' : ''} transition-colors flex items-center`}
                        title="Previous event"
                        disabled={!hasPrevEvent}
                    >
                        <ChevronLeft size={18} />
                    </JWButton>
                    <JWButton
                        size="small"
                        onClick={() => navigateToEvent('next')}
                        className={`text-[15px] ${hasNextEvent ? 'bg-[#2A2B3C] text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'} px-3 py-2 rounded-md ${hasNextEvent ? 'hover:bg-[#373951]' : ''} transition-colors flex items-center`}
                        title="Next event"
                        disabled={!hasNextEvent}
                    >
                        <ChevronRight size={18} />
                    </JWButton>
                    {dailyEvents.length > 0 && canEdit && (
                        <JWButton
                            onClick={createEvent}
                            size="small"
                            className="text-[15px] bg-[#2A2B3C] text-white px-3 py-2 rounded-md hover:bg-[#373951] transition-colors flex items-center gap-1 whitespace-nowrap"
                        >
                            <AddButtonIcon width={18} height={18} fill="currentColor" />
                            <span className="inline-block">Add Event</span>
                        </JWButton>
                    )}
                </div>
            </div>
            <hr className="mb-4 border-gray-200" />
            {isLoading ? (
                <div className="flex items-center justify-center h-full py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-eerieblack" />
                </div>
            ) : dailyEvents.length > 0 ? (
                <div className="space-y-4">
                    {dailyEvents.map((event) => (
                        <div
                            key={event._id}
                            className="jwEventItem bg-gradient-to-r from-white to-gray-50 rounded-lg shadow-sm p-4 border border-gray-200"
                        >
                            {event.flyer_url && (
                                <img
                                    src={event.flyer_url}
                                    alt={event.title}
                                    className="w-full h-48 object-cover rounded-lg mb-4"
                                />
                            )}
                            <div className="flex flex-row justify-between items-start">
                                <div className="flex-col">
                                    <p className="text-2xl font-bold text-eerieblack">
                                        {event.title}
                                    </p>
                                    <time className="text-gray-500 text-sm flex items-center gap-1">
                                        <ClockIcon />
                                        {new Date(event.dateTime).toLocaleTimeString('en-US', {
                                            hour: 'numeric',
                                            minute: '2-digit',
                                            hour12: true
                                        })}
                                    </time>
                                    {event.location && (
                                        <div className="text-gray-600 mt-2 flex items-center gap-1">
                                            <LocationPinIcon />
                                            {event.location}
                                        </div>
                                    )}
                                </div>
                                {canEdit && (
                                    <div className="flex gap-2">
                                        <button
                                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                            onClick={() => openEventModal(event)}
                                        >
                                            <PencilEditIcon
                                                width={20}
                                                height={20}
                                                fill={'#626262'}
                                                fillOpacity={0.8}
                                            />
                                        </button>
                                        <button
                                            className="p-2 hover:bg-red-50 rounded-full transition-colors"
                                            onClick={() => deleteEvent(event)}
                                        >
                                            <TrashIcon />
                                        </button>
                                    </div>
                                )}
                            </div>
                            {event.description && (
                                <div className="mt-4 p-3 bg-white bg-opacity-80 rounded-md border-l-4 border-[#2A2B3C] shadow-sm">
                                    <p className="text-gray-700 leading-relaxed">{event.description}</p>
                                </div>
                            )}
                            {event.link && (
                                <JWButton
                                    className="mt-4 text-[15px] bg-[#2A2B3C] text-white px-4 py-2 rounded-md hover:bg-[#373951] transition-colors flex items-center gap-2 whitespace-nowrap"
                                    onClick={() => window.open(event.link, '_blank')}
                                    size="medium"
                                >
                                    <ExternalLink size={16} />
                                    Go to event page
                                </JWButton>
                            )}
                        </div>
                    ))}
                </div>
            ) : defaultChildren}
        </div>
    );
}

// Define the type for the JWCalendarWithEvents props
interface JWCalendarWithEventsProps {
    calendarValue: Value;
    setCalendarValue: (value: Value) => void;
    eventsList: EventType[];
    createEvent: () => void;
    openEventModal: (event: EventType) => void;
    fetchEvents: () => void;
    canEdit?: boolean;
}

// Helper component to expose JWCalendar and JWEventsList together
export function JWCalendarWithEvents({ calendarValue, setCalendarValue, eventsList, createEvent, openEventModal, fetchEvents, canEdit = false }: JWCalendarWithEventsProps) {
    const [view, setView] = useState<'month' | 'year' | 'decade' | 'century'>('month');

    return (
        <div className='flex justify-between items-center md:flex-row flex-col -mt-2'>
            <div className="w-full max-w-[450px]">
                <JWCalendar
                    calendarValue={calendarValue}
                    setCalendarValue={setCalendarValue}
                    eventsList={eventsList}
                    view={view}
                    onViewChange={setView}
                />
            </div>
            <JWEventsList
                calendarValue={calendarValue}
                setCalendarValue={setCalendarValue}
                createEvent={createEvent}
                openEventModal={openEventModal}
                eventsList={eventsList}
                fetchEvents={fetchEvents}
                canEdit={canEdit}
                setCalendarView={setView}
            />
        </div>
    );
}

export { JWCalendar, JWEventsList };
